import type { ErrorReporter } from '@wix/cli-error-reporting';
import type { BiLogger } from '@wix/cli-telemetry';
import { Command } from '@commander-js/extra-typings';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isType, match } from 'variant';
import packageJson from '../package.json' with { type: 'json' };
import { login } from './commands/login.js';
import { logout } from './commands/logout.js';
import { telemetry } from './commands/telemetry.js';
import { whoami } from './commands/whoami.js';
import { helpConfiguration } from './help.js';
import { identifyProgramFlow } from './program-flow.js';

interface ProgramOptions {
  biLogger: BiLogger;
  errorReporter: ErrorReporter;
  projectFolder: string;
}

export async function createProgram({
  biLogger,
  errorReporter,
  projectFolder,
}: ProgramOptions) {
  const program = new Command();

  program.name('wix');
  program.version(
    packageJson.version,
    '-v, --version',
    'Output the version number'
  );

  // By default commander doesn't care about positional options
  // so if we had a `--bar` in `wix --bar` root command, or in a `wix foo --bar` sub-command
  // commander would treat them both the same.
  program.enablePositionalOptions(true);
  program.helpOption('-h, --help', 'Display help for command');
  program.helpCommand(false);
  program.configureHelp(helpConfiguration);

  const flow = await identifyProgramFlow(projectFolder);

  await match(flow, {
    APP: async ({ getRootCommand }) => {
      const app = await getRootCommand(
        { biLogger, errorReporter },
        packageJson.version
      );
      program.addCommand(app);
    },
    ASTRO: async ({ getRootCommand }) => {
      const astro = await getRootCommand(
        { biLogger, errorReporter },
        packageJson.version
      );
      for (const command of astro.commands) {
        program.addCommand(command);
      }
    },
    SITE: async ({ getRootCommand }) => {
      const site = await getRootCommand(
        { biLogger, errorReporter },
        packageJson.version
      );
      for (const command of site.commands) {
        program.addCommand(command);
      }
    },
    UNKNOWN: ({ reason }) => {
      program.on('command:*', () => {
        throw new CliError({
          cause: undefined,
          code: CliErrorCode.FailedToIdentifyProgramFlow({ reason }),
        });
      });
    },
  });

  program
    .addCommand(login({ biLogger, errorReporter }))
    .addCommand(whoami({ biLogger, errorReporter }))
    .addCommand(logout({ biLogger, errorReporter }))
    .addCommand(telemetry());

  const hasCliAppInstalled = isType(flow, 'APP');
  return { hasCliAppInstalled, program };
}
